import { Suspense, lazy, useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AppShell, useMe, SignIn } from './shell';
import { fetchMe } from './lib/auth';
import type { AuthState } from './lib/auth';
import { Card, EmptyState, ErrorState, SkeletonStat, SkeletonText } from './components';
import { RouteBoundary } from './RouteBoundary';
import { NAV_ITEMS } from './shell/Nav';
import { ApiError } from './lib/api';
import type { ModuleName } from './lib/types';

/* --------------------------------------------------------------------------
   Screens are authored separately, one per route, each default-exporting its
   component. A screen is either a single file or a directory with an
   `index.tsx`, and every one of them has grown into a directory:

       web/src/screens/Pulse/index.tsx      →  /
       web/src/screens/Expenses/index.tsx   →  /expenses
       web/src/screens/Reports/index.tsx    →  /reports
       web/src/screens/Compare/index.tsx    →  /compare
       web/src/screens/Cash/index.tsx       →  /cash
       web/src/screens/Occupancy/index.tsx  →  /occupancy
       web/src/screens/Admin/index.tsx      →  /admin

   They are resolved through a glob rather than a static import so the app
   builds and runs before any of them exist — a missing screen renders a
   placeholder instead of breaking the whole bundle. Drop the file in and it
   is picked up with no change here.

   The lookup used to be an exact property access against the glob's keys,
   which are the literal on-disk paths. On a case-insensitive filesystem — every
   Mac — `screens/pulse/` opens fine in an editor, imports fine, chunks fine,
   and then **silently renders the Placeholder**, because the key is lowercase
   and the lookup is not. Five of the seven screens hit that trap while they
   were being written. So the resolver falls back to a case-insensitive match
   and says so in dev rather than pretending the screen does not exist.
   ----------------------------------------------------------------------- */

type ScreenModule = { default: ComponentType };

const screenModules = {
  ...import.meta.glob<ScreenModule>('./screens/*.tsx'),
  ...import.meta.glob<ScreenModule>('./screens/*/index.tsx'),
};

function Placeholder({ name }: { name: string }) {
  return (
    <div className="screen">
      <div className="screen-head">
        <div>
          <h1 className="screen-title">{name}</h1>
          <p className="screen-sub">This screen has not landed yet.</p>
        </div>
      </div>
      <Card>
        <EmptyState
          title={`${name} is not built yet`}
          mark="◷"
          body={<>Add <code>web/src/screens/{name}/index.tsx</code> with a default export and it appears here — the route, the shell and the data layer are already wired. The directory name is case-sensitive on anything but a Mac.</>}
        />
      </Card>
    </div>
  );
}

function screen(name: string): ComponentType {
  const exact = `./screens/${name}.tsx`;
  const asDir = `./screens/${name}/index.tsx`;
  let loader = screenModules[exact] ?? screenModules[asDir];

  if (!loader) {
    // Case-insensitive fallback, so a directory that differs only in case
    // still routes instead of quietly showing "not built yet".
    const want = [exact.toLowerCase(), asDir.toLowerCase()];
    const hit = Object.keys(screenModules).find((k) => want.includes(k.toLowerCase()));
    if (hit) {
      loader = screenModules[hit];
      if (import.meta.env.DEV) {
        console.warn(
          `[router] "${hit}" resolves ${name} only because the lookup is case-insensitive. ` +
          `Rename it to "${asDir}" — a case-sensitive filesystem (CI, Linux) will not match it.`,
        );
      }
    }
  }

  if (!loader) return () => <Placeholder name={name} />;
  return lazy(loader);
}

const Pulse = screen('Pulse');
const Expenses = screen('Expenses');
const Reports = screen('Reports');
const Compare = screen('Compare');
const Cash = screen('Cash');
const Occupancy = screen('Occupancy');
const Documents = screen('Documents');
const Aircraft = screen('Aircraft');
const Ask = screen('Ask');
const Ledger = screen('Ledger');
const Banking = screen('Banking');
const Tickets = screen('Tickets');
const LineLeap = screen('LineLeap');
const Payroll = screen('Payroll');
const Beverage = screen('Beverage');
const SalesTax = screen('SalesTax');
const Budget = screen('Budget');
const Apar = screen('Apar');
const Admin = screen('Admin');

/** Loading a route shows the shape of a screen, never a spinner. */
function ScreenSkeleton() {
  return (
    <div className="screen">
      <SkeletonStat size="hero" />
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
        {[0, 1, 2, 3].map((i) => <Card key={i}><SkeletonStat /></Card>)}
      </div>
      <Card><SkeletonText lines={5} /></Card>
    </div>
  );
}

/**
 * Deny by default, in the UI as well as the API: a module the registry has
 * not granted does not render, and says so plainly rather than 404ing.
 *
 * The order of the two checks matters. If the identity call itself failed,
 * `can()` denies everything — so testing permission first reports an outage
 * as a permissions problem, and tells someone to go and ask an administrator
 * for access they already have. The failure is surfaced as the failure it is,
 * with a retry, and only a registry that answered can deny anything.
 */
function Guard({ module, children }: { module: ModuleName; children: React.ReactNode }) {
  const { me, loading, error, reload, can } = useMe();

  if (loading && !me) return <ScreenSkeleton />;

  if (error && !me) {
    return (
      <div className="screen">
        <div className="screen-head">
          <div>
            <h1 className="screen-title">Not loading</h1>
            <p className="screen-sub">
              This is the sign-in registry failing, not a permission you are missing.
            </p>
          </div>
        </div>
        <Card><ErrorState error={error} onRetry={reload} /></Card>
      </div>
    );
  }

  if (!can(module)) {
    return (
      <div className="screen">
        <Card><ErrorState error={new ApiError('forbidden', 'This module has not been granted to you.')} /></Card>
      </div>
    );
  }
  return <>{children}</>;
}

function moduleFor(path: string): ModuleName {
  return NAV_ITEMS.find((i) => i.to === path)?.module ?? 'venue_ops';
}

function guarded(path: string, Screen: ComponentType) {
  return (
    // The key is load-bearing — see RouteBoundary: without it a boundary that
    // caught on one route would stay caught after navigating to another,
    // because every route's element sits at the same position in the tree.
    <RouteBoundary key={path}>
      <Guard module={moduleFor(path)}>
        <Suspense fallback={<ScreenSkeleton />}><Screen /></Suspense>
      </Guard>
    </RouteBoundary>
  );
}

/**
 * Nothing renders until the server says who you are.
 *
 * The check is a real request to /api/me — never the mock — so the gate cannot
 * be satisfied by a fixture. An unreachable server is its own state rather than
 * "signed out": bouncing someone to a sign-in page over a network blip is both
 * wrong and infuriating.
 */
function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'checking' });
  useEffect(() => {
    const ac = new AbortController();
    fetchMe(ac.signal).then(setState).catch(() => {});
    return () => ac.abort();
  }, []);
  return state;
}

function Booting() {
  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center',
                  background: 'var(--ink-900)' }} aria-busy="true">
      <span className="sr-only">Checking your session</span>
    </div>
  );
}

export default function App() {
  const auth = useAuth();
  if (auth.status === 'checking') return <Booting />;
  if (auth.status !== 'signed-in') return <SignIn state={auth} />;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={guarded('/', Pulse)} />
          <Route path="/expenses" element={guarded('/expenses', Expenses)} />
          <Route path="/reports" element={guarded('/reports', Reports)} />
          <Route path="/compare" element={guarded('/compare', Compare)} />
          <Route path="/cash" element={guarded('/cash', Cash)} />
          <Route path="/occupancy" element={guarded('/occupancy', Occupancy)} />
          {/* Three screens still to land. The route, the guard and the data
              layer are wired; each renders the Placeholder until someone drops
              `web/src/screens/<Name>/index.tsx` in. Directory names are
              case-sensitive everywhere but a Mac. */}
          <Route path="/documents/*" element={guarded('/documents', Documents)} />
          <Route path="/documents" element={guarded('/documents', Documents)} />
          <Route path="/aircraft/*" element={guarded('/aircraft', Aircraft)} />
          <Route path="/aircraft" element={guarded('/aircraft', Aircraft)} />
          <Route path="/ask" element={guarded('/ask', Ask)} />
          <Route path="/ledger/*" element={guarded('/ledger', Ledger)} />
          <Route path="/ledger" element={guarded('/ledger', Ledger)} />
          <Route path="/banking/*" element={guarded('/banking', Banking)} />
          <Route path="/banking" element={guarded('/banking', Banking)} />
          <Route path="/lineleap" element={guarded('/lineleap', LineLeap)} />
          <Route path="/tickets/*" element={guarded('/tickets', Tickets)} />
          <Route path="/tickets" element={guarded('/tickets', Tickets)} />
          <Route path="/payroll/*" element={guarded('/payroll', Payroll)} />
          <Route path="/payroll" element={guarded('/payroll', Payroll)} />
          <Route path="/beverage/*" element={guarded('/beverage', Beverage)} />
          <Route path="/beverage" element={guarded('/beverage', Beverage)} />
          <Route path="/salestax/*" element={guarded('/salestax', SalesTax)} />
          <Route path="/salestax" element={guarded('/salestax', SalesTax)} />
          <Route path="/budget/*" element={guarded('/budget', Budget)} />
          <Route path="/budget" element={guarded('/budget', Budget)} />
          <Route path="/apar/*" element={guarded('/apar', Apar)} />
          <Route path="/apar" element={guarded('/apar', Apar)} />
          <Route path="/admin" element={guarded('/admin', Admin)} />
          <Route
            path="*"
            element={
              <div className="screen">
                <Card>
                  <EmptyState
                    title="No such screen"
                    body="The link may be from an older version of the app."
                    action={{ label: 'Back to Pulse', onClick: () => { window.location.href = '/'; } }}
                  />
                </Card>
              </div>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
